import { appColors } from '../../../../../globalStyles'
import { hexToRGBA } from '@utils/colorConversions'

import get from 'lodash/get'
import some from 'lodash/some'

import { ChartTypes } from '../index'

const CHART_EVENT_POINT_RADIUS = 6
const CHART_EVENT_ERROR_POINT_RADIUS = 7

export const CHART_NAMES = {
  SCENARIO_FORECAST: "Scenario",
  ACTUAL_FORECAST: "Actual",
  EXPENSE_FORECAST: "Expense",
  INCOME_FORECAST: "Income",
  JOINT_FORECAST: "Joint"
}

export const buildDatasetConfig = (colors, enabledStatus, {
  chartType
}) => {
  const sharedConfig = buildSharedConfig()

  const config = {
    [`${CHART_NAMES.ACTUAL_FORECAST}_1`]: {
      enabled: enabledStatus[`${CHART_NAMES.ACTUAL_FORECAST}_1`],
      label: chartType === ChartTypes.income ? 'Actual 1 net income' : 'Actual 1 capital',
      fill: true,
      borderColor: colors.secondaryTwoColor,
      backgroundColor: colors.secondaryTwoColor,
      borderWidth: 0.5,
      showCashflowEvents: true,
      ...sharedConfig
    },
    [`${CHART_NAMES.ACTUAL_FORECAST}_2`]: {
      enabled: enabledStatus[`${CHART_NAMES.ACTUAL_FORECAST}_2`],
      label: chartType === ChartTypes.income ? 'Actual 2 net income' : 'Actual 2 capital',
      fill: true,
      borderWidth: 0.5,
      backgroundColor: hexToRGBA(colors.secondaryOneColor),
      borderColor: hexToRGBA(colors.secondaryOneColor),
      showCashflowEvents: !enabledStatus[CHART_NAMES.SCENARIO_FORECAST],
      ...sharedConfig
    },
    [CHART_NAMES.JOINT_FORECAST]: {
      enabled: enabledStatus[CHART_NAMES.JOINT_FORECAST],
      label: 'Joint net income',
      fill: true,
      borderColor: colors.secondaryTwoColor,
      backgroundColor: colors.secondaryTwoColor,
      borderWidth: 0.5,
      showCashflowEvents: true,
      ...sharedConfig
    },
    [CHART_NAMES.INCOME_FORECAST]: {
      enabled: enabledStatus[CHART_NAMES.INCOME_FORECAST],
      label: 'Joint gross income',
      fillCb: (chart) => {
        const metasets = chart._metasets

        // If net income is hidden, enable a fill to that dataset
        // On the dashboard, We look for Actual net income, and on a scenario
        // we look for Scenario net income. 
        //
        // If they are hidden, we disable the flow. If they are not hidden, 
        // we apply fill as '-1' to income forecast. This will only fill the area
        // between the two datasets.
        const otherMetaset = metasets && metasets.find((metaset) => metaset.label === 'Joint net income')
        if (otherMetaset) {
          return otherMetaset.hidden ? false : '-1'
        }
        return false
      },
      borderColor: appColors.system,
      backgroundColor: hexToRGBA(appColors.red, 0.4),
      borderWidth: 3,
      showCashflowEvents: false,
      ...sharedConfig
    },
    [CHART_NAMES.EXPENSE_FORECAST]: {
      enabled: enabledStatus[CHART_NAMES.EXPENSE_FORECAST],
      label: 'Joint total expenses',
      fill: false,
      borderColor: appColors.system,
      borderWidth: 3,
      borderDash: [5, 5],
      showCashflowEvents: false,
      ...sharedConfig
    }
  }
  return config
}

const buildSharedConfig = () => {
  return {
    tension: 0.1,
    pointRadius: pointRadiusCallback,
    pointHoverRadius: pointRadiusCallback,
    pointBackgroundColor: pointBackgroundColorCallback,
    pointBorderColor: appColors.white,
    pointBorderWidth: 3,
    pointHoverBorderWidth: 3,
  }
}

// See: https://www.chartjs.org/docs/latest/charts/line.html#point-styling
// See: https://www.chartjs.org/docs/latest/general/options.html#scriptable-options
export const pointRadiusCallback = (ctx) => {
  const cashflowEvents = get(ctx, 'dataset.showCashflowEvents', false) && get(ctx, 'raw.cashflowEvents', [])
  const cashflowEventsDrawStatus = get(ctx, 'dataset.showCashflowEvents', false) && get(ctx, 'raw.cashflowEventsDrawStatus', {})

  const  { partial, fulfilled } = cashflowEventsDrawStatus

  if (some(cashflowEvents, { status: 'active' })) {
    return (partial || !fulfilled) ? CHART_EVENT_ERROR_POINT_RADIUS : CHART_EVENT_POINT_RADIUS
  }

  // A living annuity at its legislated maximum is the point past which the
  // household's income cannot be lifted, so mark it even with no cashflow event.
  return get(ctx, 'raw.livingAnnuityAtMax', false) ? CHART_EVENT_POINT_RADIUS : 0
}

export const pointBackgroundColorCallback = (ctx) => {
  const cashflowEvents = get(ctx, 'dataset.showCashflowEvents', false) && get(ctx, 'raw.cashflowEvents', [])
  const cashflowEventsDrawStatus = get(ctx, 'dataset.showCashflowEvents', false) && get(ctx, 'raw.cashflowEventsDrawStatus', {})

  // Where a cashflow event and a maxed living annuity land on the same point the
  // event keeps its own colour — it is the more specific thing to say about it.
  if (!some(cashflowEvents, { status: 'active' })) {
    if (get(ctx, 'raw.livingAnnuityAtMax', false)) return appColors.warning
  }

  const { partial, fulfilled } = cashflowEventsDrawStatus
  if (!fulfilled) {
    return appColors.red
  } else if (partial && fulfilled) {
    return appColors.warning
  } else {
    return appColors.success
  }
}
